import { jsx, Fragment } from "react/jsx-runtime";
import { useField, useMutationState, useEnvironment } from "@contember/binding";
import { FieldContainer } from "@contember/ui";
import { memo, useMemo } from "react";
import { useLabelMiddleware } from "../../environment/LabelMiddleware.js";
import { useAccessorErrors } from "../../errors/useAccessorErrors.js";
const SimpleRelativeSingleFieldProxy = memo(
  ({
    render,
    // Used by useField():
    defaultValue,
    isNonbearing,
    onBeforeUpdate,
    onInitialize,
    onUpdate,
    // Used only by FieldContainer:
    description,
    display,
    direction,
    evenly,
    footer,
    horizontal,
    gap,
    label,
    labelDescription,
    labelPosition,
    reverse,
    useLabelElement,
    width,
    // Used only by render():
    className,
    componentClassName,
    style,
    // Used by both render() and FieldContainer:
    errors,
    field: fieldName,
    required,
    size,
    // Used by this proxy:
    containerClassName,
    containerStyle,
    suppressErrors,
    // Rest should be empty:
    ...rest
  }) => {
    const labelMiddleware = useLabelMiddleware();
    const normalizedLabel = useMemo(() => labelMiddleware(label), [labelMiddleware, label]);
    const useFieldProps = { defaultValue, field: fieldName, isNonbearing, onBeforeUpdate, onInitialize, onUpdate };
    const field = useField(useFieldProps);
    const fieldErrors = useAccessorErrors(field);
    const combinedErrors = !suppressErrors ? combineErrorMessages(errors, fieldErrors) : void 0;
    const fieldContainerProps = {
      description,
      display,
      direction,
      errors: combinedErrors,
      evenly,
      footer,
      horizontal,
      gap,
      label: normalizedLabel,
      labelDescription,
      labelPosition,
      required,
      reverse,
      size,
      useLabelElement,
      width,
      className: containerClassName,
      componentClassName,
      style: containerStyle
    };
    const renderProps = { field: fieldName, className, style, required, size, errors, ...rest };
    const isMutating = useMutationState();
    const environment = useEnvironment();
    const fieldMetadata = useMemo(
      () => ({
        field,
        environment,
        isMutating
      }),
      [environment, field, isMutating]
    );
    const rendered = render(fieldMetadata, renderProps);
    return /* @__PURE__ */ jsx(Fragment, { children: rendered && /* @__PURE__ */ jsx(FieldContainer, { ...fieldContainerProps, children: rendered }) });
  }
);
SimpleRelativeSingleFieldProxy.displayName = "SimpleRelativeSingleFieldProxy";
function combineErrorMessages(errors, fieldErrors) {
  if (!errors && !fieldErrors) {
    return void 0;
  } else {
    return [
      ...errors ?? [],
      ...fieldErrors ?? []
    ];
  }
}
export {
  SimpleRelativeSingleFieldProxy
};
//# sourceMappingURL=SimpleRelativeFieldProxy.js.map
